<template lang="pug">
  .pho-file-upload-preview
    v-row(no-gutters)
      v-col(cols="12")
        .upload-progress.text-center.pb-8
          v-progress-circular(
            v-if="!linear"
            :rotate="-90"
            :size="150"
            :width="15"
            :value="file.progress"
            :color="progressColor")
            | {{ circularProgressBarText }}
          div(v-else)
            v-progress-linear(
              v-if="!showProcessingText"
              :value="file.progress"
              :color="progressColor"
              :height="25"
              :data-test-id="LoadingsTestIds.LOADING_PROGRESS_VIDEO"
            )
              template(v-slot:default="{value}")
                strong {{ getLinearProgressBarText(value) }}
            v-alert(v-else type="info" :icon="false")
              | {{ $t($lang.file.UPLOADED_FILES_ARE_BEING_PROCESSED_PLEASE_WAIT) }}
      v-col(cols="12")
        .text-center
          v-btn.text-capitalize(:disabled="isCancelButtonDisabled" :color="cancelButtonColor" @click="onCancel")
            | {{ cancelButtonText }}
</template>

<script>
import { useContext } from '@/compat';
import { computed } from 'vue';
import useConfirm from '@/helpers/misc/dialog/useConfirm';
import { LoadingsTestIds } from '@/components/loadings/fixed-bar/constants';

export default {
  name: 'PhoFileUploadPreview',
  props: {
    file: {
      type: Object,
      default: () => ({
        progress: 0,
      }),
    },
    linear: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['onCancel'],
  setup(props, { emit }) {
    const { $i18n, $lang } = useContext();

    const confirm = useConfirm();
    const isUploadStarted = computed(() => props.file.progress > 0);
    const isUploaded = computed(() => props.file.progress === 100);
    const isUploading = computed(() => props.file.progress < 100);
    const progressColor = computed(() => (isUploaded.value ? 'success' : 'primary'));
    const isCancelButtonDisabled = computed(() => !isUploadStarted.value || isUploaded.value);
    const cancelButtonColor = computed(() => (isCancelButtonDisabled.value ? 'primary' : 'error'));
    const cancelButtonText = computed(() => (isCancelButtonDisabled.value ? $i18n.t($lang.app.PLEASE_WAIT) : $i18n.t($lang.app.CANCEL)));
    const askUserToCancel = confirm.warning();
    const showProcessingText = computed(() => isUploadStarted.value && isUploaded.value);

    const circularProgressBarText = computed(() => (isUploading.value
      ? `%${Math.ceil(props.file.progress)}`
      : `${$i18n.t($lang.app.PROCESSING)}...`
    ));

    const getLinearProgressBarText = value => `%${Math.ceil(value)}`;

    const onCancel = async () => {
      if (await askUserToCancel(
        $i18n.t($lang.app.WARNING),
        $i18n.t($lang.file.ARE_YOU_SURE_YOU_WANT_TO_CANCEL_UPLOAD_IN_PROGRESS),
      )) {
        emit('onCancel', props.file.id);
      }
    };

    return {
      isUploadStarted,
      isUploaded,
      progressColor,
      isCancelButtonDisabled,
      cancelButtonColor,
      cancelButtonText,
      showProcessingText,
      circularProgressBarText,
      getLinearProgressBarText,
      onCancel,
      LoadingsTestIds,
    };
  },
};
</script>
